/**
 * PATH: packages/ui/src/components/SplitDrawer.tsx
 * FILE_ID: UI-LAYOUT-SPLITDRAWER-001
 * PURPOSE: Universal Cursor-style resizable split drawer chrome (promoted
 *          from apps/crm CrmSplitDrawer — UAT P2-a).
 * WHY: Right-side panels across hubs (CRM quote rail, AI triage, kanban
 *      card drawer, email reader) must behave identically: one drawer that
 *      can be PINNED as a split pane (no backdrop, drag-to-resize, pushes
 *      the page) or float as a modal overlay. Each surface re-implementing
 *      its own shell is how we got 3 drawer chromes + 12 bespoke modals
 *      with conflicting z-indices. This is the canonical container.
 * FLOW:
 *   ┌──────────────┐   ┌─────────────────────────┐   ┌────────────────────┐
 *   │ open + pin?  │ → │ portal: split | overlay │ → │ header + children  │
 *   └──────────────┘   └─────────────────────────┘   └────────────────────┘
 * DEPENDENCIES:
 *   - react-dom createPortal: escape transformed stacking contexts so the
 *     drawer sits above fixed footers/docks.
 *   - lucide-react (Pin/PinOff/X): pin toggle + close affordances.
 * RELATED:
 *   - apps/crm/src/components/layout/CrmSplitDrawer.tsx: deprecated thin
 *     re-export of this file (Expand→Migrate→Contract step 2).
 *   - packages/ui/src/kanban/zLayers.ts: kanban consumers pass Z_LAYER
 *     tokens via zBackdrop/zPanel (P2-b migration).
 */

import { useCallback, useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Pin, PinOff, X, type LucideIcon } from 'lucide-react'
import { cn } from '../lib/utils'

export interface SplitDrawerProps {
  isOpen: boolean
  onClose: () => void
  /** Pinned = split pane (no backdrop, resizable, pushes page). Unpinned = modal overlay. */
  isPinned: boolean
  onPinnedChange?: (pinned: boolean) => void
  width: number
  onWidthChange?: (w: number) => void
  minWidth?: number
  maxWidth?: number
  /** Header (desktop). Pass showHeader={false} when the child renders its own. */
  showHeader?: boolean
  title?: string
  subtitle?: string
  icon?: LucideIcon
  /** Extra header controls rendered left of the pin/close buttons. */
  headerActions?: ReactNode
  /** Mobile-only title bar (mobile is always a full-width overlay). */
  mobileTitle?: string
  /**
   * Stacking order. Defaults preserve the original CRM chrome (which must
   * beat the hub footer dock). Kanban consumers pass Z_LAYER.drawerOverlay /
   * Z_LAYER.drawer so the drawer slots correctly under menus + dialogs.
   */
  zBackdrop?: number
  zPanel?: number
  children: ReactNode
}

/**
 * clampWidth: (proposed, min, max) -> bounded width. Pure; unit-testable.
 */
export function clampWidth(proposed: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, proposed))
}

export function SplitDrawer({
  isOpen,
  onClose,
  isPinned,
  onPinnedChange,
  width,
  onWidthChange,
  minWidth = 320,
  maxWidth = 640,
  showHeader = true,
  title,
  subtitle,
  icon: Icon,
  headerActions,
  mobileTitle,
  zBackdrop = 9998,
  zPanel = 9999,
  children,
}: SplitDrawerProps) {
  const isDragging = useRef(false)
  const startX = useRef(0)
  const startWidth = useRef(width)
  // Hold the live drag teardown so an unmount mid-drag (e.g. the panel is
  // closed while the user is dragging the handle) can detach the document
  // listeners and release the body cursor/userSelect lock. Without this the
  // page is left unscrollable/uninteractable with no visible overlay —
  // the same class of failure as BUG-2026-06-03-CRM-RAIL-CLOSE-SCROLL-FREEZE,
  // now owned by the shared chrome so every consumer inherits the safe teardown.
  const dragCleanupRef = useRef<(() => void) | null>(null)

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    isDragging.current = true
    startX.current = e.clientX
    startWidth.current = width
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    const handleMove = (ev: MouseEvent) => {
      if (!isDragging.current) return
      // Drawer is anchored to the right edge, so dragging left grows it.
      const delta = startX.current - ev.clientX
      onWidthChange?.(clampWidth(startWidth.current + delta, minWidth, maxWidth))
    }
    const handleUp = () => {
      isDragging.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      document.removeEventListener('mousemove', handleMove)
      document.removeEventListener('mouseup', handleUp)
      dragCleanupRef.current = null
    }
    dragCleanupRef.current = handleUp
    document.addEventListener('mousemove', handleMove)
    document.addEventListener('mouseup', handleUp)
  }, [width, minWidth, maxWidth, onWidthChange])

  // Defensive teardown on unmount: if the drawer is removed mid-drag, release
  // any in-flight listeners + body locks so we never strand the page.
  useEffect(() => () => { dragCleanupRef.current?.() }, [])

  if (!isOpen) return null

  return createPortal(
    <>
      {/* Backdrop: always on mobile; on desktop only when floating (unpinned). */}
      <div
        className={cn(
          'fixed inset-0 bg-black/50 transition-opacity',
          isPinned && 'lg:hidden',
        )}
        style={{ zIndex: zBackdrop }}
        onClick={onClose}
      />

      <div
        className={cn(
          'fixed right-0 top-0 bottom-0',
          'bg-white dark:bg-slate-900',
          'border-l border-slate-200 dark:border-slate-700 shadow-2xl',
          'flex flex-col overflow-hidden',
          'animate-in slide-in-from-right duration-200',
        )}
        style={{ width: `${width}px`, maxWidth: '100vw', zIndex: zPanel }}
      >
        {/* Resize handle (left edge) — only meaningful as a split pane. */}
        {isPinned && (
          <div
            className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize z-10 hover:bg-teal-500/20 active:bg-teal-500/30 transition-colors group hidden lg:block"
            onMouseDown={handleResizeStart}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize panel"
          >
            <div className="absolute left-0.5 top-1/2 -translate-y-1/2 w-0.5 h-8 rounded-full bg-slate-300 dark:bg-slate-600 group-hover:bg-teal-500 transition-colors" />
          </div>
        )}

        {/* Mobile title bar (full-width overlay always has a close affordance). */}
        {mobileTitle && (
          <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300">{mobileTitle}</span>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500" aria-label="Close panel">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {showHeader && (
          <div className="flex items-center justify-between gap-2 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3">
            <div className="flex min-w-0 items-center gap-2">
              {Icon && <Icon className="h-4 w-4 shrink-0 text-teal-600 dark:text-teal-400" />}
              <div className="min-w-0">
                {title && <h2 className="truncate text-sm font-bold text-slate-900 dark:text-slate-100">{title}</h2>}
                {subtitle && <p className="truncate text-[11px] text-slate-500">{subtitle}</p>}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {headerActions}
              {onPinnedChange && (
                <button
                  onClick={() => onPinnedChange(!isPinned)}
                  title={isPinned ? 'Unpin (float over content)' : 'Pin (split alongside content)'}
                  aria-label={isPinned ? 'Unpin panel' : 'Pin panel'}
                  className={cn(
                    'hidden lg:flex h-7 w-7 items-center justify-center rounded-lg transition-colors',
                    isPinned
                      ? 'bg-teal-50 text-teal-600 dark:bg-teal-900/30 dark:text-teal-300'
                      : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-600',
                  )}
                >
                  {isPinned ? <Pin className="h-3.5 w-3.5" /> : <PinOff className="h-3.5 w-3.5" />}
                </button>
              )}
              <button
                onClick={onClose}
                aria-label="Close panel"
                className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-hidden">{children}</div>
      </div>
    </>,
    document.body,
  )
}

export default SplitDrawer
